import React, { useState, useEffect } from 'react';
import PopupCalendar from './PopupCalendar';
import ApiService from '../../services/api-service';
import 'react-calendar/dist/Calendar.css';
import './CalendarSection.scss';

function CalendarSection({ userId, loading, setLoading }) {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    duration: '',
    location: '',
  });
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showCalendarPopup, setShowCalendarPopup] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  // Add this function after the fetchEvents function

const handleDeleteEvent = async (eventId) => {
  if (!confirm('Are you sure you want to delete this event?')) {
    return;
  }
  
  setLoading(true);
  try {
    await ApiService.DELETE_CALENDAR_EVENT(eventId);
    // Update local state to remove the deleted event
    setEvents(prevEvents => prevEvents.filter(event => event._id !== eventId));
    alert('Event deleted successfully!');
  } catch (error) {
    alert('Failed to delete event: ' + (error.message || 'Unknown error'));
    console.error('Delete event error:', error);
  }
  setLoading(false);
};

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) {
      alert('User ID is missing. Please log in and try again.');
      return;
    }
    setLoading(true);
    try {
      const startTime = new Date(`${formData.date}T${formData.time}:00Z`);
      const durationInMs = parseInt(formData.duration || 60) * 60 * 1000;
      const endTime = new Date(startTime.getTime() + durationInMs);
      const payload = {
        userId,
        title: formData.title,
        description: '',
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        location: formData.location || '',
      };
      await ApiService.CREATE_CALENDAR_EVENT(payload);
      alert('Event added successfully!');
      setFormData({
        title: '',
        date: '',
        time: '',
        duration: '',
        location: '',
      });
      fetchEvents();
    } catch (error) {
      alert('Failed to add event. Check the console for details.');
      console.error(error);
    }
    setLoading(false);
  };

  // Fetch events from the backend
  const fetchEvents = async () => {
    if (!userId) return;
    try {
      const res = await ApiService.FETCH_CALENDAR_EVENTS(userId);
      setEvents(res && res.events ? res.events : []);
    } catch (error) {
      setEvents([]);
    }
  };

  // Fetch events immediately when component mounts or when userId changes  
  useEffect(() => {
    if (userId) {
      fetchEvents();
    }
  }, [userId]);

  // Toggle the schedule form
  const toggleScheduleForm = () => {
    setShowScheduleForm((prev) => !prev);
    setShowCalendarPopup(false);
  };

  // Open the popup calendar
  const openCalendarPopup = () => {
    fetchEvents();
    setShowCalendarPopup(true);
    setShowScheduleForm(false);
  };

  // Close the popup calendar
  const closeCalendarPopup = () => {
    setShowCalendarPopup(false);
  };

  // Retrieve up to 5 upcoming events (sorted by start time)
  const upcomingEvents = [...events]
    .filter((ev) => new Date(ev.startTime) > new Date())
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
    .slice(0, 5);

  return (
    <div className="calendar-section">
      <div className="button-group">
        <button className="schedule-btn" onClick={toggleScheduleForm}>
          {showScheduleForm ? 'Hide Schedule Form' : 'Schedule'}
        </button>
        <button className="calendar-btn" onClick={openCalendarPopup}>
          Calendar
        </button>
      </div>

<div className="upcoming-events-list">
  <h4>Upcoming</h4>
  {upcomingEvents.length === 0 ? (
    <div className="no-upcoming">No upcoming events.</div>
  ) : (
    <ul>
      {upcomingEvents.map((ev) => (
        <li key={ev._id || ev.startTime}>
          <div className="event-item">
            <div className="event-info">
              <strong>{ev.title}</strong>
              <span>
                {new Date(ev.startTime).toLocaleString([], {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
                {ev.location ? ` @ ${ev.location}` : ''}
              </span>
            </div>
            <button 
              className="delete-btn" 
              onClick={() => handleDeleteEvent(ev._id)}
              aria-label="Delete event"
              disabled={loading}
            >
              ✕
            </button>
          </div>
        </li>
      ))}
    </ul>
  )}
</div>

      {/* Schedule Form */}
      {showScheduleForm && (
        <form onSubmit={handleSubmit} className="schedule-form">
          <div>
            <label>Title:</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label>Date:</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label>Time:</label>
            <input
              type="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <label>Duration (minutes):</label>
            <input
              type="number"
              name="duration"
              placeholder="e.g., 60"
              value={formData.duration}
              onChange={handleChange}
            />
          </div>
          <div>
            <label>Location:</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
            />
          </div>
          <button type="submit" disabled={loading}>
            {loading ? 'Adding...' : 'Add Event'}
          </button>
        </form>
      )}

      {/* Popup Calendar */}
      {showCalendarPopup && (
        <PopupCalendar events={events} onClose={closeCalendarPopup} />
      )}
    </div>
  );
}

export default CalendarSection;